import React, { useState } from 'react'
import { Breadcrumb, ProgressIndicator, Modal } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import RewardSetup from '@/containers/Rewards/RewardSetup/RewardSetup'
import RewardDisplay from '@/containers/Rewards/RewardDisplay/RewardDisplay'
import RewardAttributes from '@/containers/Rewards/RewardAttributes/RewardAttributes'
import RewardReview from '@/containers/Rewards/RewardReview/RewardReview'
import { useRewards } from '@/hooks/useRewards'
import { MODAL_TYPE } from '@/utils/constants/globalStyle'
import { ROUTES } from '@/utils/constants/routesConstants'

/**
 * CreateReward component displays the multi-step reward creation process.
 * Maintains consistent structure across all steps with dynamic content rendering.
 *
 * @returns {JSX.Element} The rendered CreateReward component.
 */
const CreateReward = () => {
  const [isCancel, setIsCancel] = useState(false)

  const {
    currentStep,
    setStep,
    handleNext,
    handleDraft,
    handleCancel,
    hasFormErrors,
  } = useRewards()

  const handleCancelClick = () => {
    setIsCancel(true)
  }

  const handleCancelConfirm = () => {
    handleCancel()
    setIsCancel(false)
  }

  const handleCancelReject = () => {
    setIsCancel(false)
  }

  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <RewardSetup />
      case 2:
        return <RewardDisplay />
      case 3:
        return <RewardAttributes />
      case 4:
        return <RewardReview />
      default:
        return <RewardSetup />
    }
  }

  // Get step titles for progress indicator
  const stepTitles = [
    { title: 'Setup' },
    { title: 'Display' },
    { title: 'Attributes' },
    { title: 'Review' },
  ]

  // Check if current step is review (last step)
  const isReviewStep = currentStep === 4

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              href: '/',
              label: '',
            },
            {
              href: ROUTES.REWARD_CATALOG,
              label: 'Reward Catalog',
            },
            {
              href: '',
              label: 'Create Reward',
            },
          ],
        }}
        id={'create_reward_breadcrumb'}
      />

      <PageHeader wrapperClassName="mt-0 h-12" title={'Create Reward'} />
      <ProgressIndicator
        currentStep={currentStep}
        completed={false}
        steps={4}
        titles={stepTitles}
        isDark={true}
        className='p-6'
        onStepClick={(step) => {
          setStep(step)
        }}
      />

      <div className="step-content">
        {renderStepContent()}
      </div>

      {isCancel && (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isCancel}
          title="Are you sure you want to cancel?"
          text={'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO'}
          showPrimaryButton={true}
          primaryButtonText="Yes"
          onClickPrimaryButton={handleCancelConfirm}
          showSecondaryButton={true}
          secondaryButtonText="No"
          onClickSecondaryButton={handleCancelReject}
          footerProps={{
            size: 'medium',
            type: 'primary',
          }}
          isDark={true}
        />
      )}
      <div className="h-[144px] flex items-end">
      <SaveCancelFooter
        handleCancel={handleCancelClick}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={hasFormErrors()}
        isReview={isReviewStep}
        wrapperClassName="w-full"
      />
      </div>
    </div>
  )
}

export default CreateReward
